<template>
    <div class="evaluation-panel">
        <div v-if="item" class="panel-content">
            <div class="panel-scrollable">
                <!-- Header del item -->
                <div class="item-header">
                    <div class="item-title-section">
                        <h4 class="item-title">{{ item.name }}</h4>
                        <p class="item-breadcrumb">
                            Pilar: {{ pillarName }} / Elemento: {{ elementName }}
                        </p>
                    </div>
                    <button class="info-button" @click="showInfo = !showInfo">
                        <i class="fa" :class="showInfo ? 'fa-chevron-up' : 'fa-info-circle'"></i>
                    </button>
                </div>

                <!-- Panel de información colapsable -->
                <div v-if="showInfo" class="info-panel">
                    <div class="info-row">
                        <strong>Pregunta:</strong>
                        <span>{{ item.question }}</span>
                    </div>
                    <div class="info-row">
                        <strong>Criterio transversal:</strong>
                        <span>{{ getCrossCuttingCriteria() }}</span>
                    </div>
                    <div class="info-row">
                        <strong>Ruta de visita:</strong>
                        <span>{{ getTourRoutes() }}</span>
                    </div>
                    <div class="info-row">
                        <strong>Legislación que aplica:</strong>
                        <span>{{ item.default_legislation || 'No especificada' }}</span>
                    </div>
                </div>

                <!-- Opciones del auditor -->
                <div class="rating-section">
                    <h5 class="section-title">Opciones del auditor: nivel y por qué</h5>
                    <div class="rating-grid" v-if="ratingDefinitions.length">
                        <div v-for="rating in ratingDefinitions" :key="rating.id" class="rating-card"
                            :class="{ 'selected': selectedRatingId === rating.id }" @click="selectRating(rating)">
                            <div class="rating-header">
                                <h6 class="rating-title">{{ rating.name }}</h6>
                                <span v-if="!rating.is_not_applicable" class="rating-value"
                                    :style="{ backgroundColor: getRatingColor(rating.id) }">
                                    {{ formatRatingValue(rating) }}
                                </span>
                            </div>
                            <div class="rating-guide-container">
                                <p class="rating-guide" :class="{ 'expanded': expandedGuides[rating.id] }">
                                    {{ expandedGuides[rating.id] ? getFullGuideForRating(rating.id) :
                                        getTruncatedGuide(rating.id) }}
                                </p>
                                <button v-if="guideNeedsTruncation(rating.id)" class="btn-ver-mas"
                                    @click.stop="toggleGuideExpansion(rating.id)">
                                    {{ expandedGuides[rating.id] ? 'Ver menos' : 'Ver más' }}
                                </button>
                            </div>
                        </div>
                    </div>
                    <p v-else class="text-muted small">Sin escalas configuradas para este diagnóstico.</p>
                </div>

                <!-- Campos de evaluación -->
                <div class="evaluation-fields">
                    <div class="reset-row">
                        <button class="btn-reset-defaults" type="button" :disabled="!selectedRatingId"
                            @click="resetToDefaults">
                            <i class="fa fa-history"></i>
                            Restaurar textos sugeridos
                        </button>
                    </div>
                    <!-- Observaciones -->
                    <div class="form-group">
                        <label class="field-label">
                            Se evidencia con
                            <span class="optional-badge">Opcional</span>
                        </label>
                        <textarea v-model="localResponse.observations" class="form-control" rows="6"
                            placeholder="Describe las evidencia relevantes..." @input="handleTextInput()"></textarea>
                    </div>
                    <!-- Plan de Acción -->
                    <div class="form-group" v-if="showPlanAction">
                        <label class="field-label">
                            Plan de Acción
                            <span class="optional-badge">Opcional</span>
                        </label>
                        <textarea v-model="localResponse.action_plan" class="form-control" rows="6"
                            placeholder="Describa el plan de acción para mejorar este ítem..."
                            @input="handleTextInput()"></textarea>
                    </div>

                    <!-- Herramienta Tecnológica -->
                    <div class="form-group">
                        <label class="field-label">
                            Herramienta Tecnológica Implementada
                            <span class="optional-badge">Opcional</span>
                        </label>
                        <textarea v-model="localResponse.technological_tool" class="form-control" rows="5"
                            placeholder="Describa las herramientas tecnológicas utilizadas..."
                            @input="handleTextInput()"></textarea>
                    </div>

                    <!-- Archivos de Evidencia (Nuevo Componente) -->
                    <item-documents v-if="item.response && item.response.id" :key="item.id + '-docs'"
                        :documents="item.response.documents || []" :diagnostic-documents="diagnosticDocuments"
                        :item-response-id="item.response.id" :diagnostic-id="item.response.diagnostic_id"
                        :company-id="companyId" @change="handleDocumentsChanged" />
                </div>
            </div>

            <!-- Estado de guardado -->
            <div class="autosave-status" v-if="isSaving">
                <i class="fa fa-spinner fa-spin"></i>
                Guardando cambios...
            </div>
        </div>

        <!-- Empty state -->
        <div v-else class="empty-panel">
            <i class="fa fa-hand-pointer-o fa-4x"></i>
            <p>Selecciona un ítem de la lista para comenzar la evaluación</p>
        </div>

    </div>
</template>

<script>
import ItemDocuments from './ItemDocuments.vue';

export default {
    name: 'EvaluationPanel',
    components: {
        ItemDocuments
    },
    props: {
        item: {
            type: Object,
            default: null
        },
        pillarName: {
            type: String,
            default: ''
        },
        elementName: {
            type: String,
            default: ''
        },
        companyId: {
            type: [Number, String],
            default: null
        },
        ratingDefinitions: {
            type: Array,
            default: () => []
        },
        diagnosticDocuments: {
            type: Array,
            default: () => []
        }
    },
    data() {
        return {
            showInfo: false,
            selectedRatingId: null,
            expandedGuides: {},
            localResponse: {
                observations: '',
                action_plan: '',
                technological_tool: ''
            },
            autoSaveTimeout: null,
            isSaving: false,
            isInitializingResponse: false
        };
    },
    computed: {
        highestRatingValue() {
            if (!this.ratingDefinitions || !this.ratingDefinitions.length) {
                return null;
            }

            return this.ratingDefinitions.reduce((max, rating) => {
                if (typeof rating.value === 'number' && rating.value > max) {
                    return rating.value;
                }
                return max;
            }, -Infinity);
        },

        showPlanAction() {
            const rating = this.ratingDefinitions.find(r => r.id === this.selectedRatingId);
            if (!rating) {
                return true;
            }

            const isNotApplicable = rating.is_not_applicable === true || rating.is_not_applicable === 1;
            const isHighestValue = typeof rating.value === 'number' && rating.value === this.highestRatingValue;

            return !(isNotApplicable || isHighestValue);
        }
    },
    watch: {
        item: {
            immediate: true,
            handler() {
                this.clearAutosaveTimer();
                if (this.item) {
                    this.loadItemData();
                }
            }
        },
        localResponse: {
            handler() {
                if (this.isInitializingResponse) {
                    return;
                }
                this.handleTextInput();
            },
            deep: true
        }
    },
    beforeDestroy() {
        this.clearAutosaveTimer();
    },
    methods: {
        loadItemData() {
            this.isInitializingResponse = true;
            // Si el item ya tiene una respuesta guardada, cargarla
            if (this.item.response && this.item.response.rating) {
                this.selectedRatingId = this.item.response.rating.id;
                const response = this.item.response;
                const defaults = this.getDefaultsForRating(this.item.response.rating.id);
                const isSynced = response && response.is_synced;

                this.localResponse = {
                    observations: isSynced
                        ? (response.observations || '')
                        : (response.observations != null ? response.observations : (defaults.observation || '')),
                    action_plan: isSynced
                        ? (response.action_plan || '')
                        : (response.action_plan != null ? response.action_plan : (defaults.actionPlan || '')),
                    technological_tool: response.technological_tool || ''
                };
            } else {
                // Resetear
                this.selectedRatingId = null;
                this.localResponse = {
                    observations: '',
                    action_plan: '',
                    technological_tool: ''
                };
            }

            this.$nextTick(() => {
                this.isInitializingResponse = false;
            });
        },

        async selectRating(rating) {
            this.selectedRatingId = rating.id;

            const response = this.item && this.item.response ? this.item.response : null;
            const hasMatchingResponse = response && response.rating && response.rating.id === rating.id;
            const isSyncedResponse = response && response.is_synced;

            if (hasMatchingResponse) {
                this.isInitializingResponse = true;
                this.localResponse = {
                    observations: response.observations || '',
                    action_plan: response.action_plan || '',
                    technological_tool: response.technological_tool || ''
                };
                this.$nextTick(() => {
                    this.isInitializingResponse = false;
                });

                // Siempre guardar si el usuario hace clic explícitamente, para asegurar sincronización
                await this.saveEvaluation(true);
                return;
            }

            if (isSyncedResponse) {
                // Mantener textos actuales; solo guardar cambio de nivel
                await this.saveEvaluation(true);
                return;
            }

            this.isInitializingResponse = true;
            const nextResponse = {
                observations: '',
                action_plan: '',
                technological_tool: response && response.technological_tool ? response.technological_tool : ''
            };

            if (this.item && this.item.default_for_rating) {
                const observations = this.item.default_for_rating.observations;
                const actionPlans = this.item.default_for_rating.action_plans;

                const defaultObservation = observations && observations.find(function (obs) {
                    return obs.rating_id === rating.id;
                });
                const defaultActionPlan = actionPlans && actionPlans.find(function (plan) {
                    return plan.rating_id === rating.id;
                });

                if (defaultObservation) {
                    nextResponse.observations = defaultObservation.observation || '';
                }
                if (defaultActionPlan) {
                    nextResponse.action_plan = defaultActionPlan.action_plan || '';
                }
            }

            this.localResponse = nextResponse;
            this.$nextTick(() => {
                this.isInitializingResponse = false;
            });

            await this.saveEvaluation(true);
        },

        getFullGuideForRating(ratingId) {
            if (!this.item || !this.item.auditor_guides) return '';
            const guide = this.item.auditor_guides.find(g => g.rating_id === ratingId);
            return guide ? guide.guide : 'Sin guía disponible';
        },

        getTruncatedGuide(ratingId) {
            const text = this.getFullGuideForRating(ratingId);
            if (text.length > 120) {
                return text.substring(0, 120) + '...';
            }
            return text;
        },

        guideNeedsTruncation(ratingId) {
            const text = this.getFullGuideForRating(ratingId);
            return text.length > 120;
        },

        toggleGuideExpansion(ratingId) {
            this.$set(this.expandedGuides, ratingId, !this.expandedGuides[ratingId]);
        },

        getCrossCuttingCriteria() {
            if (!this.item || !this.item.cross_cutting_criteria) return 'N/A';
            return this.item.cross_cutting_criteria.map(c => c.name).join(', ');
        },

        getTourRoutes() {
            if (!this.item || !this.item.tour_routes) return 'N/A';
            return this.item.tour_routes.map(r => r.name).join(', ');
        },

        getRatingColor(ratingId) {
            const rating = this.ratingDefinitions.find(r => r.id === ratingId);
            return rating && rating.color ? rating.color : '#9CA3AF';
        },

        formatRatingValue(rating) {
            if (rating == null) {
                return '-';
            }
            if (typeof rating.value !== 'undefined') {
                return rating.value;
            }
            if (typeof rating.rating !== 'undefined') {
                return rating.rating;
            }
            return '-';
        },

        async saveEvaluation(immediate = false) {
            if (!this.selectedRatingId || !this.item) {
                return;
            }

            this.clearAutosaveTimer();

            this.isSaving = true;
            const evaluation = {
                item_id: this.item.id,
                rating_id: this.selectedRatingId,
                ...this.localResponse
            };

            // Emitir al componente padre; se asume que gestiona la llamada async
            this.$emit('save-evaluation', evaluation, { immediate });

            // Dar un pequeño margen antes de ocultar el indicador
            setTimeout(() => {
                this.isSaving = false;
            }, 300);
        },

        handleTextInput() {
            if (!this.selectedRatingId) {
                return;
            }
            this.scheduleAutosave();
        },

        scheduleAutosave() {
            this.clearAutosaveTimer();
            this.autoSaveTimeout = setTimeout(() => {
                this.saveEvaluation(true);
            }, 800);
        },

        clearAutosaveTimer() {
            if (this.autoSaveTimeout) {
                clearTimeout(this.autoSaveTimeout);
                this.autoSaveTimeout = null;
            }
        },

        resetToDefaults() {
            if (!this.item || !this.selectedRatingId) {
                return;
            }

            this.isInitializingResponse = true;

            const response = this.item.response || null;
            const defaults = this.getDefaultsForRating(this.selectedRatingId);

            this.localResponse = {
                observations: defaults.observation,
                action_plan: defaults.actionPlan,
                technological_tool: response && response.technological_tool ? response.technological_tool : ''
            };

            this.$nextTick(() => {
                this.isInitializingResponse = false;
                this.saveEvaluation();
            });
        },

        getDefaultsForRating(ratingId) {
            const fallback = { observation: '', actionPlan: '' };
            if (!this.item || !this.item.default_for_rating) {
                return fallback;
            }

            const observations = this.item.default_for_rating.observations;
            const actionPlans = this.item.default_for_rating.action_plans;

            const defaultObservation = observations && observations.find(obs => obs.rating_id === ratingId);
            const defaultActionPlan = actionPlans && actionPlans.find(plan => plan.rating_id === ratingId);

            return {
                observation: defaultObservation ? (defaultObservation.observation || '') : '',
                actionPlan: defaultActionPlan ? (defaultActionPlan.action_plan || '') : ''
            };
        },

        handleDocumentsChanged() {
            // Emitir evento para que el padre recargue los datos
            this.$emit('file-uploaded');
        }
    },
    computed: {
        highestRatingValue() {
            if (!this.ratingDefinitions || !this.ratingDefinitions.length) {
                return null;
            }

            return this.ratingDefinitions.reduce((max, rating) => {
                if (typeof rating.value === 'number' && rating.value > max) {
                    return rating.value;
                }
                return max;
            }, -Infinity);
        },

        showPlanAction() {
            const rating = this.ratingDefinitions.find(r => r.id === this.selectedRatingId);
            if (!rating) {
                return true;
            }

            const isNotApplicable = rating.is_not_applicable === true || rating.is_not_applicable === 1;
            const isHighestValue = typeof rating.value === 'number' && rating.value === this.highestRatingValue;

            return !(isNotApplicable || isHighestValue);
        }
    }
};
</script>

<style scoped>
.evaluation-panel {
    background: white;
    border: 1px solid #E5E7EB;
    border-radius: 8px;
    max-height: calc(100vh - 260px);
    overflow-y: auto;
}

.panel-content {
    padding: 24px;
}

.panel-scrollable {
    max-height: calc(100vh - 320px);
    overflow-y: auto;
    padding-right: 8px;
}

/* Header */
.item-header {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    gap: 16px;
    margin-bottom: 32px;
    padding-bottom: 24px;
    border-bottom: 1px solid #E5E7EB;
}

.item-title-section {
    flex: 1;
}

.item-title {
    font-size: 16px;
    font-weight: 500;
    color: #1F2937;
    margin: 0 0 8px 0;
    line-height: 1.4;
}

.item-breadcrumb {
    font-size: 12px;
    color: #495263;
    margin: 0;
}

.info-button {
    background: #F3F4F6;
    border: 1px solid #E5E7EB;
    border-radius: 50%;
    width: 40px;
    height: 40px;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: all 0.15s ease;
    flex-shrink: 0;
}

.info-button:hover {
    background: #E5E7EB;
    border-color: #D1D5DB;
}

.info-button i {
    font-size: 18px;
    color: #6B7280;
}

/* Panel de información colapsable */
.info-panel {
    background: #F9FAFB;
    border: 1px solid #E5E7EB;
    border-radius: 8px;
    padding: 16px;
    margin-bottom: 24px;
}

.info-row {
    display: grid;
    grid-template-columns: 180px 1fr;
    gap: 12px;
    padding: 8px 0;
    border-bottom: 1px solid #E5E7EB;
    font-size: 14px;
}

.info-row:last-child {
    border-bottom: none;
}

.info-row strong {
    color: #1F2937;
    font-weight: 600;
}

.info-row span {
    color: #6B7280;
    line-height: 1.5;
}

/* Rating section */
.rating-section {
    margin-bottom: 32px;
}

.section-title {
    font-size: 16px;
    font-weight: 600;
    color: #1F2937;
    margin-bottom: 16px;
}

.rating-grid {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 16px;
}

.rating-card {
    background: white;
    border: 2px solid #E5E7EB;
    border-radius: 8px;
    padding: 16px;
    cursor: pointer;
    transition: all 0.15s ease;
}

.rating-card:hover {
    border-color: #10B981;
    box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
}

.rating-card.selected {
    border-color: #10B981;
    border-width: 3px;
    background-color: #F0FDF4;
}

.rating-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 8px;
}

.rating-title {
    font-size: 13px;
    font-weight: 600;
    color: #1F2937;
    margin: 0;
}

.rating-value {
    display: inline-block;
    padding: 2px 5px;
    border-radius: 4px;
    font-size: 12px;
    font-weight: 700;
    color: white;
}

.rating-guide-container {
    display: flex;
    flex-direction: column;
}

.rating-guide {
    font-size: 13px;
    color: #6B7280;
    line-height: 1.5;
    margin: 0;
}

.rating-guide.expanded {
    display: block;
}

.btn-ver-mas {
    background: none;
    border: none;
    color: #10B981;
    font-size: 12px;
    font-weight: 600;
    cursor: pointer;
    padding: 4px 0 0 0;
    text-align: left;
    transition: color 0.15s ease;
    outline: none;
}

.btn-ver-mas:hover {
    color: #059669;
    text-decoration: underline;
}

.btn-ver-mas:focus {
    outline: none;
    box-shadow: none;
}

/* Evaluation fields */
.evaluation-fields {
    margin-bottom: 24px;
}

.reset-row {
    display: flex;
    justify-content: flex-end;
    margin-bottom: 12px;
}

.btn-reset-defaults {
    background: transparent;
    border: 1px solid #D1D5DB;
    border-radius: 6px;
    padding: 6px 12px;
    font-size: 13px;
    color: #4B5563;
    display: inline-flex;
    align-items: center;
    gap: 6px;
    cursor: pointer;
    transition: all 0.15s ease;
}

.btn-reset-defaults:hover:not(:disabled) {
    background: #F9FAFB;
    border-color: #9CA3AF;
}

.btn-reset-defaults:disabled {
    opacity: 0.6;
    cursor: not-allowed;
}

.form-group {
    margin-bottom: 20px;
}

.field-label {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 14px;
    font-weight: 600;
    color: #1F2937;
    margin-bottom: 8px;
}

.optional-badge {
    background: #F3F4F6;
    color: #6B7280;
    font-size: 11px;
    font-weight: 500;
    padding: 2px 8px;
    border-radius: 12px;
}

.form-control {
    width: 100%;
    padding: 12px;
    border: 1px solid #D1D5DB;
    border-radius: 6px;
    font-size: 14px;
    color: #1F2937;
    transition: all 0.15s ease;
    resize: vertical;
}

.form-control:focus {
    outline: none;
    border-color: #10B981;
    box-shadow: 0 0 0 3px rgba(16, 185, 129, 0.1);
}

.form-control::placeholder {
    color: #9CA3AF;
}


.autosave-status {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 13px;
    color: #6B7280;
    margin-top: 12px;
}

/* Empty state */
.empty-panel {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    height: 100%;
    color: #9CA3AF;
    padding: 48px;
    text-align: center;
}

.empty-panel i {
    margin-bottom: 24px;
    opacity: 0.5;
}

.empty-panel p {
    font-size: 16px;
    margin: 0;
}


/* Responsive */
@media (max-width: 1200px) {
    .rating-grid {
        grid-template-columns: repeat(2, 1fr);
    }
}

@media (max-width: 768px) {
    .rating-grid {
        grid-template-columns: 1fr;
    }

    .detail-row {
        grid-template-columns: 1fr;
        gap: 4px;
    }
}

/* Scrollbar */
.evaluation-panel::-webkit-scrollbar {
    width: 6px;
}

.evaluation-panel::-webkit-scrollbar-track {
    background: #F9FAFB;
}

.evaluation-panel::-webkit-scrollbar-thumb {
    background: #D1D5DB;
    border-radius: 3px;
}

.evaluation-panel::-webkit-scrollbar-thumb:hover {
    background: #9CA3AF;
}
</style>
